import { useState, useEffect, useCallback } from "react";
import { db } from "../firebase.config.js";
import { collection, query, where, onSnapshot } from "firebase/firestore";

// 📋 SHARED QUERY CONFIGURATION
const DEFAULT_COLLECTION = "document_tasks";

/**
 * Builds a query for counting unread documents with a specific status
 * @param {string} orgId - Organization/Bucket ID (used as bucket value in Firestore)
 * @param {string} status - Status to filter by ("completed" or "failed")
 * @param {string} collectionName - Collection name
 * @returns {Query} Firestore query for counting
 */
const buildStatusCountQuery = (orgId, status, collectionName = DEFAULT_COLLECTION) => {
   return query(
      collection(db, collectionName),
      where("bucket", "==", orgId),
      where("status", "==", status),
      where("readAt", "==", false)
   );
};

/**
 * useTaskNotificationCount Hook
 *
 * Provides real-time counts of unread completed and failed documents.
 * Uses Firestore onSnapshot for live updates when data changes.
 *
 * @param {string|null} orgId - Organization/Bucket ID to filter documents by bucket field. Must be a non-empty string.
 * @param {string} [collectionName="document_tasks"] - Firestore collection name
 * @returns {Object} { completedCount: number, failedCount: number, loading: boolean, error: Error|null }
 *
 * @example
 * const { completedCount, failedCount, loading, error } = useTaskNotificationCount(orgId);
 * // counts update automatically when documents are added/removed/modified
 */
export default function useTaskNotificationCount(orgId, collectionName = "document_tasks") {
   const [completedCount, setCompletedCount] = useState(0);
   const [failedCount, setFailedCount] = useState(0);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);

   // Track loading state for each query separately
   const [completedLoading, setCompletedLoading] = useState(true);
   const [failedLoading, setFailedLoading] = useState(true);

   /**
    * Validates orgId and returns a normalized version or throws an error
    */
   const validateOrgId = useCallback((orgId) => {
      if (orgId === null || orgId === undefined) {
         throw new Error("orgId is required but was null/undefined");
      }

      if (typeof orgId !== 'string') {
         throw new Error(`orgId must be a string, got ${typeof orgId}: ${orgId}`);
      }

      const trimmed = orgId.trim();
      if (!trimmed) {
         throw new Error("orgId cannot be empty or whitespace-only");
      }

      return trimmed;
   }, []);

   /**
    * Categorizes Firestore errors for better debugging
    */
   const categorizeFirestoreError = useCallback((err) => {
      const errorCode = err?.code || err?.message || 'unknown';

      if (errorCode.includes('permission-denied')) {
         return new Error(`Firestore permission denied: Check security rules for collection "${collectionName}". Ensure user has read access.`);
      }

      if (errorCode.includes('index')) {
         return new Error(`Firestore index error: Ensure composite index exists for collection "${collectionName}" with fields: bucket, status, readAt. Create index at: https://console.firebase.google.com`);
      }

      if (errorCode.includes('not-found')) {
         return new Error(`Firestore collection not found: "${collectionName}" collection may not exist or user lacks access.`);
      }

      if (errorCode.includes('unavailable')) {
         return new Error(`Firestore unavailable: Check network connection and Firestore service status.`);
      }

      if (errorCode.includes('resource-exhausted')) {
         return new Error(`Firestore quota exceeded: Check Firestore usage limits and billing.`);
      }

      // Return original error with additional context
      return new Error(`Firestore error: ${err?.message || errorCode}`);
   }, [collectionName]);

   // Update overall loading state when individual loading states change
   useEffect(() => {
      setLoading(completedLoading || failedLoading);
   }, [completedLoading, failedLoading]);

   useEffect(() => {
      // Reset state when orgId or collectionName changes
      setCompletedLoading(true);
      setFailedLoading(true);
      setError(null);
      setCompletedCount(0);
      setFailedCount(0);

      let validOrgId;
      try {
         validOrgId = validateOrgId(orgId);
      } catch (validationError) {
         console.warn("🔔 useTaskNotificationCount: Invalid orgId, skipping query:", validationError.message);
         setError(validationError);
         setCompletedLoading(false);
         setFailedLoading(false);
         return;
      }

      // Validate collectionName
      if (!collectionName || typeof collectionName !== 'string' || collectionName.trim() === '') {
         const collectionError = new Error(`Invalid collectionName: must be non-empty string, got: ${collectionName}`);
         console.error("🔔 useTaskNotificationCount:", collectionError.message);
         setError(collectionError);
         setCompletedLoading(false);
         setFailedLoading(false);
         return;
      }

      const validCollectionName = collectionName.trim();

      console.debug(`🔔 useTaskNotificationCount: Setting up real-time listeners for orgId: ${validOrgId}, collection: ${validCollectionName}`);

      let unsubscribeCompleted;
      let unsubscribeFailed;

      try {
         // Query 1: Completed + Unread
         const completedQuery = buildStatusCountQuery(validOrgId, "completed", validCollectionName);
         unsubscribeCompleted = onSnapshot(completedQuery, (snapshot) => {
            const count = snapshot.size;
            console.debug(`🔔 useTaskNotificationCount: Completed count update - ${count}`);
            setCompletedCount(count);
            setCompletedLoading(false);
         }, (err) => {
            const categorizedError = categorizeFirestoreError(err);
            console.error("🔔 useTaskNotificationCount: Completed query error:", categorizedError.message, err);
            setError(categorizedError);
            setCompletedCount(0);
            setCompletedLoading(false);
         });

         // Query 2: Failed + Unread
         const failedQuery = buildStatusCountQuery(validOrgId, "failed", validCollectionName);
         unsubscribeFailed = onSnapshot(failedQuery, (snapshot) => {
            const count = snapshot.size;
            console.debug(`🔔 useTaskNotificationCount: Failed count update - ${count}`);
            setFailedCount(count);
            setFailedLoading(false);
         }, (err) => {
            const categorizedError = categorizeFirestoreError(err);
            console.error("🔔 useTaskNotificationCount: Failed query error:", categorizedError.message, err);
            setError(categorizedError);
            setFailedCount(0);
            setFailedLoading(false);
         });

      } catch (err) {
         const categorizedError = categorizeFirestoreError(err);
         console.error("🔔 useTaskNotificationCount: Error setting up listeners:", categorizedError.message, err);
         setError(categorizedError);
         setCompletedCount(0);
         setFailedCount(0);
         setCompletedLoading(false);
         setFailedLoading(false);
      }

      // Return cleanup function for useEffect
      return () => {
         console.debug("🔔 useTaskNotificationCount: Cleaning up real-time listeners");
         if (unsubscribeCompleted) unsubscribeCompleted();
         if (unsubscribeFailed) unsubscribeFailed();
      };

   }, [orgId, collectionName, validateOrgId, categorizeFirestoreError]);

   return { completedCount, failedCount, loading, error };
}
